import React from 'react';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Span from '@deliveroo/consumer-component-library/components/Span';
import SpacerElement from '@deliveroo/consumer-component-library/internal/SpacerElement';

import styles from './ReferralCodeInput.scss';

/**
 * A simple component for displaying a referral code, with a button that copies
 * the value to the user’s clipboard
 */
export default class ReferralCodeInput extends React.Component {
  timer = null;

  state = {
    isCopied: false,
  };

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  onCopy = () => {
    this.setState({ isCopied: true });
    this.timer = setTimeout(() => {
      this.setState({ isCopied: false });
    }, 1000);
  };

  onButtonClick = () => {
    const { onCopyClick, copyValue } = this.props;
    onCopyClick(copyValue);
  };

  render() {
    const {
      label,
      displayValue,
      copyValue,
      copyLabel,
      copiedLabel,
      /* eslint-disable react/prop-types */
      spacing,
      adjacentVerticalSpacing,
      adjacentHorizontalSpacing,
      /* eslint-enable react/prop-types */
    } = this.props;

    const { isCopied } = this.state;

    return (
      <SpacerElement
        spacing={spacing}
        adjacentVerticalSpacing={adjacentVerticalSpacing}
        adjacentHorizontalSpacing={adjacentHorizontalSpacing}
      >
        <Span color="grey" size="small" spacing="0 0 small" isBlock>
          {label}
        </Span>
        <div className={styles.referralCodeInput}>
          <div className={styles.codeText}>
            <Span>
              <span className={styles.nowrap}>{displayValue}</span>
            </Span>
          </div>
          <div className={styles.buttonWrapper}>
            <CopyToClipboard text={copyValue} onCopy={this.onCopy}>
              <Button onClick={this.onButtonClick} isBlock>
                {isCopied ? copiedLabel : copyLabel}
              </Button>
            </CopyToClipboard>
          </div>
        </div>
      </SpacerElement>
    );
  }
}

ReferralCodeInput.propTypes = {
  /**
   * A help label to be displayed above the referral code itself.
   */
  label: PropTypes.string.isRequired,

  /**
   * The displayed value of the referral code, e.g. roo.it/foo123.
   */
  displayValue: PropTypes.string.isRequired,

  /**
   * The actual value of the referral code copied to the clipboard on copy, e.g. https://roo.it/foo123.
   */
  copyValue: PropTypes.string.isRequired,

  /**
   * The label of the action button before the user has triggered a copy.
   */
  copyLabel: PropTypes.string.isRequired,

  /**
   * The label of the action button once the user has triggered a copy.
   */
  copiedLabel: PropTypes.string.isRequired,

  /**
   * Callback function triggered when the user copies the referral code.
   */
  onCopyClick: PropTypes.func,
};

ReferralCodeInput.defaultProps = {
  onCopyClick: () => undefined,
};
